/**
 * Campaign attribution for the DUPR+ purchase funnel (ENG-498).
 *
 * A tagged visit — any URL carrying utm_* parameters, the format given to marketing partners —
 * is captured into localStorage and replayed onto every Mixpanel event by `eventTracker` for the
 * attribution window. localStorage rather than memory or sessionStorage because the funnel
 * crosses a two-factor challenge, a signup email that opens a fresh tab, and a round trip to Stripe.
 */

/** The parameters marketing partners put on their links. Keys are kept verbatim as event properties. */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
export type UtmKey = (typeof UTM_KEYS)[number]
export type UtmParams = Partial<Record<UtmKey, string>>

export interface AttributionEnvelope {
  utm: UtmParams
  /** ISO timestamp of the tagged visit. */
  capturedAt: string
  /** The full URL the tagged visit landed on. */
  landingUrl: string
}

/** Last touch: a newer tagged visit replaces an older one, and after this long the record is dropped. */
export const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

/**
 * How long the same campaign on this browser counts as one visit. The login redirect replays
 * the landing query within minutes; a return through the same link next week is a new click.
 */
export const SAME_VISIT_MS = 30 * 60 * 1000

const STORAGE_KEY = 'dupr.attribution'

/** The visit captured at import time whose landing event is still owed (see the bottom of this file). */
let pendingLanding: AttributionEnvelope | undefined

export function parseUtmParams(search: string): UtmParams | undefined {
  const query = new URLSearchParams(search)
  const utm: UtmParams = {}
  for (const key of UTM_KEYS) {
    const value = query.get(key)?.trim()
    if (value) utm[key] = value
  }
  return Object.keys(utm).length > 0 ? utm : undefined
}

/**
 * Records a tagged visit. Returns the new envelope, or undefined when the URL carries no campaign
 * parameters (the stored envelope is left alone) or the same campaign arrived again within the
 * visit window (the login redirect replaying the landing query is not a second landing).
 */
export function captureAttribution(
  url: { href: string; search: string },
  now = new Date()
): AttributionEnvelope | undefined {
  const utm = parseUtmParams(url.search)
  if (!utm) return undefined
  const envelope = { utm, capturedAt: now.toISOString(), landingUrl: url.href }
  if (isSameVisit(readEnvelope(), envelope)) return undefined
  writeEnvelope(envelope)
  return envelope
}

/** The stored envelope while it is inside the attribution window; an expired one is dropped on read. */
export function getAttribution(now = Date.now()): AttributionEnvelope | undefined {
  const envelope = readEnvelope()
  if (!envelope) return undefined
  if (isExpired(envelope, now)) {
    clearAttribution()
    return undefined
  }
  return envelope
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode, blocked site data): nothing to clear.
  }
}

/** Flattened for Mixpanel: the utm_* keys verbatim, so they line up with Mixpanel's own UTM columns. */
export function flattenAttribution(envelope: AttributionEnvelope): Record<string, string> {
  return { ...envelope.utm, utm_captured_at: envelope.capturedAt, utm_landing_url: envelope.landingUrl }
}

/** The stored campaign, flattened, for every event; empty outside the window. */
export function attributionEventProperties(now = Date.now()): Record<string, string> {
  const envelope = getAttribution(now)
  return envelope ? flattenAttribution(envelope) : {}
}

/** Hands over the import-time visit once, so its landing event is sent exactly once. */
export function takePendingLanding(): AttributionEnvelope | undefined {
  const pending = pendingLanding
  pendingLanding = undefined
  return pending
}

function isSameVisit(existing: AttributionEnvelope | undefined, next: AttributionEnvelope): boolean {
  if (!existing) return false
  const gap = Date.parse(next.capturedAt) - Date.parse(existing.capturedAt)
  return gap >= 0 && gap <= SAME_VISIT_MS && UTM_KEYS.every((key) => existing.utm[key] === next.utm[key])
}

function isExpired(envelope: AttributionEnvelope, now: number): boolean {
  const capturedAt = Date.parse(envelope.capturedAt)
  return Number.isNaN(capturedAt) || now - capturedAt > ATTRIBUTION_WINDOW_MS
}

function writeEnvelope(envelope: AttributionEnvelope): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope))
  } catch {
    // Storage unavailable: the visit is still attributed by Mixpanel's first-touch people properties.
  }
}

function readEnvelope(): AttributionEnvelope | undefined {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    return isEnvelope(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

/** Shape check for whatever came back from storage. */
function isEnvelope(value: unknown): value is AttributionEnvelope {
  const candidate = value as Partial<AttributionEnvelope> | null
  return (
    typeof candidate?.utm === 'object' &&
    candidate.utm !== null &&
    typeof candidate.capturedAt === 'string' &&
    typeof candidate.landingUrl === 'string'
  )
}

// Capture the landing URL synchronously at import time, before any route guard redirects it away.
// Its landing event waits for `AttributionCapture` to mount: Mixpanel may not be initialised yet.
if (typeof window !== 'undefined') pendingLanding = captureAttribution(window.location)
